if (!customElements.get("quick-view")) {
  customElements.define(
    "quick-view",
    class QuickView extends HTMLElement {
      getFocusableElements(parent) {
        const focusableElementsSelector =
          "button:not(.wt-product__sticky-buy__button), [href], input:not(.disabled):not([type='hidden']), select, .close-btn";
        const focusableElements = () =>
          Array.from(parent.querySelectorAll(focusableElementsSelector)).filter(
            (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
          );

        return {
          focusableElements,
          first: focusableElements()[0],
          last: focusableElements()[focusableElements().length - 1],
        };
      }

      connectedCallback() {
        // Triggers are normally the single wrapped <button> (card.liquid,
        // svc-product-card, shoppable-video). When the wrapper instead carries
        // elements tagged with [data-quick-view-trigger] — e.g. a bundle card's
        // image and title links — bind to those so other controls inside the
        // same wrapper (the Add to cart button) keep their own behaviour.
        const taggedTriggers = this.querySelectorAll("[data-quick-view-trigger]");
        const triggers = taggedTriggers.length
          ? Array.from(taggedTriggers)
          : [this.querySelector("button")].filter(Boolean);
        this.trigger_button = triggers[0];
        this.triggerHandlers = new Map();
        this.isModalOpen = false;
        this.fetchController = null;
        this.didPublishOpen = false;

        triggers.forEach((trigger) => {
          const handler = (e) => {
            const el = e.currentTarget;
            // Stop anchors (or any non-button trigger) navigating to the PDP;
            // the href stays as a no-JS / new-tab fallback.
            if (el.tagName !== "BUTTON") e.preventDefault();
            this.product_url = el.getAttribute("data-product-url");
            this.currentTrigger = el;
            this.fetchProduct(this.product_url);
          };
          this.triggerHandlers.set(trigger, handler);
          trigger.addEventListener("click", handler);
        });

        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleModalClick = this.handleModalClick.bind(this);
        this.handleModalKeydown = this.handleModalKeydown.bind(this);
        this.getFocusableElements = this.getFocusableElements.bind(this);

        this.lastFeaturedImage;
        this.firstMedia;
        this.hasFirstMediaImg;

        this.fetchProduct = this.fetchProduct.bind(this);
        this.galleryObserver = this.galleryObserver.bind(this);
        this.observerCallback = this.observerCallback.bind(this);
        this.disconnectObserver = this.disconnectObserver.bind(this);
        this.removeButtonEventListener =
          this.removeButtonEventListener.bind(this);
      }

      fetchProduct(product_url) {
        this.fetchController?.abort();
        this.lastFeaturedImage = null;
        this.firstMedia = null;
        this.hasFirstMediaImg = null;
        this.quick_view = document.querySelector(".wt__quick-view");
        this.quick_view_container = document.querySelector(
          ".wt__quick-view__container",
        );
        this.quick_view_wrapper = document.querySelector(
          ".wt__quick-view--wrapper",
        );
        this.close_button = this.quick_view.querySelector(".close-btn");
        this.loader = this.quick_view.querySelector(".wt__quick-view-loader");
        this.page_overlay = document.querySelector(".wt__quick-view--overlay");
        this.body = document.body;

        const controller = new AbortController();
        this.fetchController = controller;
        const signal = controller.signal;

        this.restoreViewAllToWrapper();
        const allWtProducts =
          this.quick_view_wrapper.querySelectorAll(".wt-product");
        allWtProducts?.forEach((product) => product.remove());

        this.loader.classList.remove("hidden");
        this.openModal();

        this.close_button.addEventListener("click", this.handleCloseClick);
        this.quick_view.addEventListener("click", this.handleModalClick);
        this.quick_view_container.addEventListener(
          "keydown",
          this.handleModalKeydown,
        );

        fetch(product_url, { signal })
          .then((res) => {
            if (!res.ok) {
              throw new Error(`Quick view request failed (${res.status})`);
            }
            return res.text();
          })
          .then((res) => {
            if (
              signal.aborted ||
              this.fetchController !== controller ||
              !this.isModalOpen
            ) {
              return;
            }
            this.loader.classList.add("hidden");
            const htmlDocument = new DOMParser().parseFromString(
              res,
              "text/html",
            );
            this.productMain = htmlDocument.querySelector(".wt-product__main");
            this.productCard = htmlDocument.querySelector(".wt-product");
            this.productInfo = htmlDocument.querySelector(".wt-product__info");
            const quickViewFeatures =
              this.buildQuickViewFeatures(htmlDocument);

            const animatedElements = this.productMain.querySelectorAll(
              ".scroll-trigger.animate--slide-in",
            );
            animatedElements?.forEach((element) =>
              element.classList.remove("scroll-trigger", "animate--slide-in"),
            );

            const productGallery =
              this.productCard.querySelector("gallery-section");

            this.removeElements(
              this.productCard,
              "collapsible-section",
              "[not-quick-add]",
              ".product__inventory",
              ".share-icons__container",
              "#gallery-loader",
              "pickup-availability",
              ".wt-product__sku",
              ".wt-product__feature-icons",
              ".wt-description",
              "gallery-section",
              "sticky-buy-button",
              ".wt-product__sticky-buy",
            );

            // Drop empty placeholder blocks (separators / unconfigured blocks)
            // that only add vertical gaps in the modal.
            this.productCard
              .querySelectorAll(".wt-product__info > [data-block-id]")
              .forEach((block) => {
                const hasContent = block.querySelector(
                  "img, svg, input, button, a, picture, iframe, video",
                );
                if (!block.textContent.trim() && !hasContent) {
                  block.remove();
                }
              });

            // The review-stars badge and brand line are cloned from the product
            // page. Reviews live on the full product page (the "View all details"
            // link), so always drop the badge in the modal: when a product has a
            // server-rendered rating it otherwise sits above the title and pushes
            // the title — and the image opposite it — down off the container's
            // top line. Brand is still dropped only when empty.
            this.productCard
              .querySelectorAll(".wt-product__info .jdgm-preview-badge")
              .forEach((badge) => {
                (badge.closest(".shopify-app-block") || badge).remove();
              });
            this.productCard
              .querySelectorAll(".wt-product__info .wt-product__brand")
              .forEach((brand) => {
                if (!brand.textContent.trim()) brand.remove();
              });

            this.insertQuickViewFeatures(quickViewFeatures);

            const newVariantId =
              this.productCard.querySelector("variant-options");
            if (newVariantId) {
              newVariantId.dataset.updateUrl = "false";
              this.updateAttribute(
                newVariantId,
                "data-original-section",
                newVariantId.getAttribute("data-section"),
              );
              this.updateAttribute(
                newVariantId,
                "data-section",
                `quickview-${newVariantId.getAttribute("data-section")}`,
              );
            }

            productGallery.style.display = "none";

            this.images = productGallery.querySelectorAll("img");
            this.updateAttribute(
              productGallery,
              "id",
              `MediaGallery-${newVariantId?.getAttribute("data-section")}`,
            );
            const firstImageFromGallery = productGallery.querySelector("li");
            if (firstImageFromGallery) {
              this.firstMedia = firstImageFromGallery;

              this.playBtn = this.firstMedia.querySelector(".model-btn");
              if (this.playBtn) this.playBtn.setAttribute("tabindex", "0");

              this.video = this.firstMedia.querySelector("video");
              if (this.video) this.video.setAttribute("tabindex", "0");

              this.hasFirstMediaImg = Boolean(
                firstImageFromGallery.querySelector("img"),
              );
            }

            firstImageFromGallery
              ?.querySelector("img")
              ?.removeAttribute("srcset");

            // Build the left media column (main image + optional thumbnails).
            // The natural .wt-product__info order is kept intact so the right
            // column reads like the real product page (title, price, variants,
            // qty, add-to-cart) without any reshuffle.
            const imageContainer = document.createElement("div");
            imageContainer.setAttribute("class", "product-image");
            imageContainer.innerHTML = firstImageFromGallery?.innerHTML || "";

            const media = document.createElement("div");
            media.setAttribute("class", "wt__quick-view__media");
            media.appendChild(imageContainer);

            const slides = productGallery
              ? Array.from(productGallery.querySelectorAll("li"))
              : [];
            const thumbSources = [];
            const seenThumbSrc = new Set();
            slides.forEach((slide) => {
              const slideImg = slide.querySelector("img");
              if (!slideImg) return;
              const fullSrc =
                slideImg.getAttribute("src") ||
                slideImg.getAttribute("data-src");
              if (!fullSrc || seenThumbSrc.has(fullSrc)) return;
              seenThumbSrc.add(fullSrc);
              thumbSources.push(fullSrc);
            });

            if (thumbSources.length > 1) {
              const thumbs = document.createElement("div");
              thumbs.setAttribute("class", "wt__quick-view__thumbs");
              thumbSources.forEach((src, index) => {
                const thumbBtn = document.createElement("button");
                thumbBtn.setAttribute("type", "button");
                thumbBtn.setAttribute(
                  "class",
                  `wt__quick-view__thumb${index === 0 ? " wt__quick-view__thumb--active" : ""}`,
                );
                thumbBtn.setAttribute("aria-label", `Image ${index + 1}`);
                thumbBtn.setAttribute("data-thumb-src", src);
                const thumbImg = document.createElement("img");
                thumbImg.setAttribute("src", src);
                thumbImg.setAttribute("loading", "lazy");
                thumbImg.setAttribute("alt", "");
                thumbBtn.appendChild(thumbImg);
                thumbs.appendChild(thumbBtn);
              });
              media.appendChild(thumbs);
            }

            const mainSection =
              this.productCard.querySelector(".wt-product__main");
            if (mainSection) {
              mainSection.before(media);
            } else {
              this.productCard.prepend(media);
            }

            this.galleryObserver();

            const mainProductPrice =
              this.productCard.querySelector(".wt-product__price");
            this.updateAttribute(
              mainProductPrice,
              "id",
              `price-${newVariantId?.getAttribute("data-section")}`,
            );

            const allFieldSets = this.productCard.querySelectorAll("fieldset");
            this.updateFieldSets(allFieldSets);

            const addBtn = this.productCard.querySelector('button[name="add"]');
            this.updateAttribute(
              addBtn,
              "id",
              `ProductSubmitButton-${newVariantId?.getAttribute("data-section")}`,
            );

            const form = this.productCard.querySelector(
              'form[method="post"][data-type="add-to-cart-form"]',
            );
            this.updateAttribute(
              form,
              "id",
              `product-form-${newVariantId?.getAttribute("data-section")}`,
            );

            const subWidget = this.productCard.querySelector(
              ".wt-subscription-widget",
            );
            if (subWidget) {
              const quickSectionId = newVariantId?.getAttribute("data-section");
              const originalSectionId = newVariantId?.getAttribute(
                "data-original-section",
              );
              subWidget.setAttribute("data-section-id", quickSectionId);
              subWidget.setAttribute(
                "data-original-section-id",
                originalSectionId,
              );
              subWidget.setAttribute("data-update-url-on-plan-change", "false");

              const hiddenInput = subWidget.querySelector(
                'input[name="selling_plan"]',
              );
              if (hiddenInput) {
                hiddenInput.setAttribute(
                  "form",
                  `product-form-${quickSectionId}`,
                );
              }
            }

            const viewAllDetailsContainer =
              this.quick_view_wrapper.querySelector(
                ".wt__quick-view__view-all-container",
              );
            viewAllDetailsContainer?.remove();
            this.quick_view_wrapper.append(this.productCard);
            if (viewAllDetailsContainer) {
              this.quick_view_wrapper.append(viewAllDetailsContainer);
            }

            const giftCardInput = this.quick_view_wrapper.querySelector(
              '[name="properties[__shopify_send_gift_card_to_recipient]"]',
            );
            if (giftCardInput) giftCardInput.removeAttribute("disabled");

            this.addViewAllDetailsButton();
            this.initThumbnails();

            // Fire the quick-buy drawer–open event (reused by cart/variant logic)
            this.didPublishOpen = true;
            publish(PUB_SUB_EVENTS.quickBuyDrawerOpen, { source: "quick-view" });

            // initialize the variant options
            const variantOptions =
              this.quick_view_wrapper.querySelector("variant-options");
            if (
              variantOptions &&
              typeof variantOptions.initialize === "function"
            ) {
              variantOptions.initialize();
            }

            this.close_button.setAttribute("tabindex", "0");
            this.close_button.focus({ preventScroll: true });
            if (this.fetchController === controller) {
              this.fetchController = null;
            }
          })
          .catch((error) => {
            if (error.name === "AbortError") return;
            if (this.fetchController !== controller) return;

            this.fetchController = null;
            console.error("Quick view error:", error);
            this.closeModal();
          });
      }

      openModal() {
        if (this.isModalOpen) return;

        this.isModalOpen = true;
        this.didPublishOpen = false;
        window.wtDocumentScrollLock.lock(this);
        this.quick_view_container.scrollTop = 0;
        this.quick_view.classList.add("wt__quick-view--open");
        this.quick_view.setAttribute("aria-hidden", "false");
        this.quick_view.removeAttribute("inert");
        this.page_overlay.classList.add("wt__quick-view--overlay--open");
        this.page_overlay.setAttribute("aria-hidden", "false");
        this.body.classList.add("quick-view-open");
        this.close_button.setAttribute("tabindex", "0");
        requestAnimationFrame(() => {
          if (this.isModalOpen) {
            this.close_button.focus({ preventScroll: true });
          }
        });
      }

      addViewAllDetailsButton() {
        const viewAllDetailsContainer = this.quick_view_wrapper.querySelector(
          ".wt__quick-view__view-all-container",
        );
        const link = viewAllDetailsContainer.querySelector("a");
        link.href = this.product_url;
        link.setAttribute("tabindex", "0");
        viewAllDetailsContainer.classList.remove("hidden");

        // Keep the product title linking through to the full product page.
        const title = this.quick_view_wrapper.querySelector(
          ".wt-product__info .wt-product__name",
        );
        if (title && !title.querySelector("a")) {
          const titleLink = document.createElement("a");
          titleLink.href = this.product_url;
          while (title.firstChild) titleLink.appendChild(title.firstChild);
          title.appendChild(titleLink);
        }

        // Sit the "View all details" icon as a square button immediately to the
        // right of Add to cart, inside the same flex row.
        const addToCartWrapper = this.quick_view_wrapper.querySelector(
          ".wt-product__add-to-cart_form--wrapper",
        );
        if (addToCartWrapper) {
          addToCartWrapper.appendChild(viewAllDetailsContainer);
        } else if (title) {
          // Fallback: leave it beside the title.
          title.after(viewAllDetailsContainer);
        }
      }

      restoreViewAllToWrapper() {
        const viewAll = this.quick_view_wrapper?.querySelector(
          ".wt__quick-view__view-all-container",
        );
        if (viewAll && viewAll.parentElement !== this.quick_view_wrapper) {
          this.quick_view_wrapper.appendChild(viewAll);
        }
      }

      hideViewAllDetailsButton() {
        const viewAllDetailsContainer = this.quick_view_wrapper.querySelector(
          ".wt__quick-view__view-all-container",
        );
        viewAllDetailsContainer.classList.add("hidden");
      }

      buildQuickViewFeatures(htmlDocument) {
        const source =
          htmlDocument.querySelector(
            ".wt-product__features.wt-product__bulletpoints",
          ) || htmlDocument.querySelector(".wt-product__features");
        if (!source || !source.querySelector(".wt-product__features__item")) {
          return null;
        }

        const features = source.cloneNode(true);
        // Drop the hide-until-scrolled hooks (they'd leave it invisible in the
        // modal — same reason productMain gets them stripped), keep the global
        // .wt-product__features__* styling, add a modal-scoped hook.
        features.classList.remove(
          "scroll-trigger",
          "animate--slide-in",
          "disabled-on-mobile",
        );
        features.classList.add("wt__quick-view__features");
        features.removeAttribute("not-quick-add");
        features.removeAttribute("data-block-id");
        return features;
      }

      insertQuickViewFeatures(features) {
        if (!features) return;

        const productInfo = this.productCard.querySelector(".wt-product__info");
        if (!productInfo) return;

        const taxLine = productInfo.querySelector(".product__tax.caption.rte");
        const price = productInfo.querySelector(".wt-product__price");
        const title = productInfo.querySelector(".wt-product__name");
        const insertionPoint = taxLine || price || title;

        if (insertionPoint) {
          insertionPoint.after(features);
        } else {
          productInfo.prepend(features);
        }
      }

      initThumbnails() {
        const thumbButtons = this.quick_view_wrapper.querySelectorAll(
          ".wt__quick-view__thumb",
        );
        if (!thumbButtons.length) return;

        thumbButtons.forEach((thumbBtn) => {
          thumbBtn.setAttribute("tabindex", "0");
          thumbBtn.addEventListener("click", () => {
            const src = thumbBtn.getAttribute("data-thumb-src");
            if (!src) return;

            const mediaBox = this.quick_view_wrapper.querySelector(
              ".wt__quick-view__media .product-image",
            );
            const mainImage = mediaBox?.querySelector("img");
            if (mainImage) {
              this.swapMainImage(mediaBox, mainImage, src);
            }

            this.quick_view_wrapper
              .querySelectorAll(".wt__quick-view__thumb--active")
              .forEach((el) =>
                el.classList.remove("wt__quick-view__thumb--active"),
              );
            thumbBtn.classList.add("wt__quick-view__thumb--active");
          });
        });

        this.initThumbStripFades();
      }

      // Fade the current image out, swap the source, fade back in once the
      // new image has loaded (with a timeout fallback so a failed load can't
      // leave the gallery stuck invisible).
      swapMainImage(mediaBox, mainImage, src) {
        mediaBox.classList.add("product-image--switching");

        window.setTimeout(() => {
          const reveal = () =>
            mediaBox.classList.remove("product-image--switching");

          mainImage.setAttribute("src", src);
          mainImage.removeAttribute("srcset");

          if (mainImage.complete) {
            requestAnimationFrame(reveal);
          } else {
            mainImage.addEventListener("load", reveal, { once: true });
            window.setTimeout(reveal, 800);
          }
        }, 180);
      }

      // Toggle the strip's edge-fade masks based on scroll position so the
      // fades only appear where thumbnails actually overflow.
      initThumbStripFades() {
        const strip = this.quick_view_wrapper.querySelector(
          ".wt__quick-view__thumbs",
        );
        if (!strip) return;

        const updateFades = () => {
          const maxScroll = strip.scrollWidth - strip.clientWidth;
          strip.classList.toggle("has-fade-left", strip.scrollLeft > 4);
          strip.classList.toggle(
            "has-fade-right",
            maxScroll > 4 && strip.scrollLeft < maxScroll - 4,
          );
        };

        updateFades();
        strip.addEventListener("scroll", updateFades, { passive: true });
      }

      galleryObserver() {
        const config = {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
        };
        this.observer = new MutationObserver(this.observerCallback);
        this.observer.observe(this.quick_view_wrapper, config);
      }

      observerCallback(mutations) {
        for (const mutation of mutations) {
          if (mutation.target.localName === "variant-options") {
            this.quick_view = document.querySelector(".wt__quick-view");
            const variantOptions =
              this.quick_view.querySelector("variant-options");
            const imageContainer =
              this.quick_view.querySelector(".product-image");
            const image = imageContainer.querySelector("img");
            const formInput = this.quick_view.querySelector(
              'form input[name="id"]',
            );
            if (variantOptions.getAttribute("data-variant-id")) {
              formInput.value = variantOptions.getAttribute("data-variant-id");
              formInput.setAttribute(
                "value",
                variantOptions.getAttribute("data-variant-id"),
              );
            }
            if (
              variantOptions.getAttribute("data-featured-image") &&
              this.lastFeaturedImage !==
                variantOptions.getAttribute("data-featured-image")
            ) {
              image?.setAttribute(
                "src",
                variantOptions.getAttribute("data-featured-image"),
              );
              image?.removeAttribute("srcset");

              this.lastFeaturedImage = variantOptions.getAttribute(
                "data-featured-image",
              );
              if (
                imageContainer.children[0]?.nodeName !== "IMG" &&
                imageContainer.children[0]?.nodeName !== "A"
              ) {
                const newImage = document.createElement("img");
                newImage.setAttribute(
                  "class",
                  "wt-product__img  wt-product__img--zoom-cursor",
                );
                newImage.setAttribute(
                  "src",
                  variantOptions.getAttribute("data-featured-image"),
                );
                newImage.removeAttribute("srcset");
                imageContainer.innerHTML = "";
                imageContainer.appendChild(newImage);
              } else {
                image.setAttribute(
                  "src",
                  variantOptions.getAttribute("data-featured-image"),
                );
                image.removeAttribute("srcset");
              }
              this.lastFeaturedImage = variantOptions.getAttribute(
                "data-featured-image",
              );
            } else {
              const hasFirstMediaModel =
                this.firstMedia?.querySelector("model-element");
              if (!this.hasFirstMediaImg || hasFirstMediaModel)
                imageContainer.innerHTML = this.firstMedia?.innerHTML;
            }
            break;
          }
        }
      }

      handleCloseClick(event) {
        event.preventDefault();
        this.closeModal();
      }

      handleModalClick(event) {
        if (this.isModalOpen && event.target === this.quick_view) {
          this.closeModal();
        }
      }

      handleModalKeydown(event) {
        if (!this.isModalOpen) return;

        if (
          event.key === "Escape" ||
          event.keyCode === 27 ||
          event.code === "Escape"
        ) {
          event.preventDefault();
          this.closeModal();
          return;
        }

        const isTabPressed =
          event.key === "Tab" || event.keyCode === 9 || event.code === "Tab";
        if (!isTabPressed) return;

        const { first, last } = this.getFocusableElements(
          this.quick_view_container,
        );
        if (!first || !last) {
          event.preventDefault();
          this.close_button.focus({ preventScroll: true });
        } else if (event.shiftKey && document.activeElement === first) {
          last.focus();
          event.preventDefault();
        } else if (!event.shiftKey && document.activeElement === last) {
          first.focus();
          event.preventDefault();
        }
      }

      closeModal({ restoreFocus = true } = {}) {
        if (!this.isModalOpen && !this.fetchController) return;

        const returnFocusElement = this.currentTrigger;
        const controller = this.fetchController;
        this.isModalOpen = false;
        this.fetchController = null;
        controller?.abort();

        this.close_button?.removeEventListener("click", this.handleCloseClick);
        this.quick_view?.removeEventListener("click", this.handleModalClick);
        this.quick_view_container?.removeEventListener(
          "keydown",
          this.handleModalKeydown,
        );

        // Fire the paired close event only if content finished opening.
        if (this.didPublishOpen) {
          publish(PUB_SUB_EVENTS.quickBuyDrawerClose, { source: "quick-view" });
          this.didPublishOpen = false;
        }

        const loader = this.quick_view.querySelector(".wt__quick-view-loader");
        loader.classList.remove("hidden");

        // Move the persistent "View all details" container back out of the
        // product before removing it, so it survives close/reopen cycles.
        this.restoreViewAllToWrapper();
        const product = this.quick_view.querySelector(".wt-product");

        if (product) product.remove();

        this.body.classList.remove("quick-view-open");
        this.quick_view.classList.remove("wt__quick-view--open");
        this.quick_view.setAttribute("aria-hidden", "true");
        this.quick_view.setAttribute("inert", "");
        this.page_overlay.classList.remove("wt__quick-view--overlay--open");
        this.page_overlay.setAttribute("aria-hidden", "true");
        this.close_button.setAttribute("tabindex", "-1");
        this.hideViewAllDetailsButton();
        this.disconnectObserver();
        window.wtDocumentScrollLock.unlock(this);

        if (restoreFocus && returnFocusElement?.isConnected) {
          requestAnimationFrame(() => {
            returnFocusElement.focus({ preventScroll: true });
          });
        }
      }

      removeElements(element, ...selectors) {
        selectors.forEach((selector) => {
          const elements = element.querySelectorAll(selector);
          elements.forEach((el) => el.remove());
        });
      }

      updateAttribute(element, attribute, value) {
        if (element) {
          element.setAttribute(attribute, value);
        }
      }

      updateFieldSets(fieldSets) {
        fieldSets.forEach((fieldset) => {
          const inputRadios = fieldset.querySelectorAll('input[type="radio"]');
          const labels = fieldset.querySelectorAll("label");

          inputRadios.forEach((radio) => {
            this.updateAttribute(
              radio,
              "id",
              `quickview-${radio.getAttribute("id")}`,
            );
            this.updateAttribute(
              radio,
              "form",
              `product-form-${radio.getAttribute("id")}`,
            );
          });

          labels.forEach((label) => {
            this.updateAttribute(
              label,
              "for",
              `quickview-${label.getAttribute("for")}`,
            );
          });
        });
      }

      disconnectObserver() {
        this.observer?.disconnect();
      }

      removeButtonEventListener() {
        this.triggerHandlers?.forEach((handler, trigger) => {
          trigger.removeEventListener("click", handler);
        });
        this.triggerHandlers?.clear();
      }

      disconnectedCallback() {
        this.closeModal({ restoreFocus: false });
        this.disconnectObserver();
        this.removeButtonEventListener();
      }
    },
  );
}
